import { OysterIcon } from "@oysterhr/icons"
import classNames from "classnames"
import { createContext, forwardRef, useContext } from "react"
import { Icon } from "../Icon"
import type { BadgeIconProps, BadgeProps, BadgeVariation } from "./types"

const BadgeContext = createContext<{ variation: BadgeVariation }>({ variation: "gray" })

function useBadgeContext() {
  return useContext(BadgeContext)
}

const BadgeIcon = ({ className: classNameProp, ...props }: BadgeIconProps) => {
  const { variation } = useBadgeContext()
  const iconByVariation: Record<BadgeVariation, OysterIcon> = {
    gray: "WarningOctagon",
    success: "CheckCircle",
    info: "InfoCircle",
    warning: "WarningTriangle",
    error: "WarningCircle",
  }

  const iconColorByVariation: Record<BadgeVariation, string> = {
    gray: "text-gray-400",
    success: "text-success-base",
    info: "text-info-base",
    warning: "text-warning-base",
    error: "text-error-base",
  }

  const _className = classNames(classNameProp, iconColorByVariation[variation])

  return (
    <Icon {...props} className={_className} aria-hidden="true" name={iconByVariation[variation]} />
  )
}

BadgeIcon.displayName = "Badge.Icon"

export const Badge = Object.assign(
  forwardRef<HTMLSpanElement, BadgeProps>(
    ({ children, className, isOutline, variation = "gray", size = "xs", ...rest }, ref) => {
      const isGray = variation === "gray"
      const isError = variation === "error"
      const isSuccess = variation === "success"
      const isWarning = variation === "warning"
      const isInfo = variation === "info"

      const isXS = size === "xs"
      const isSM = size === "sm"
      const isMD = size === "md"

      const _className = classNames(
        "badge",
        className,
        "rounded-full text-center inline-flex items-center justify-center gap-1 items-center font-normal",
        {
          "min-h-5 px-1.5 text-xs min-w-5": isXS,
          "min-h-6 px-2 py-0.5 text-xs min-w-6": isSM,
          "min-h-7 px-2 py-1 text-sm leading-snug min-w-7": isMD,
        },
        {
          "text-black": !isOutline,
          "bg-gray-200": isGray && !isOutline,
          "bg-error-lighten-100": isError && !isOutline,
          "bg-success-lighten-100": isSuccess && !isOutline,
          "bg-warning-lighten-100": isWarning && !isOutline,
          "bg-info-lighten-100": isInfo && !isOutline,
        },
        {
          "border bg-white": isOutline,
          "border-gray-300 text-gray-400": isGray && isOutline,
          "border-error-base text-error-darken-100": isError && isOutline,
          "border-success-base text-success-darken-100": isSuccess && isOutline,
          "border-warning-base text-warning-darken-100": isWarning && isOutline,
          "border-info-base text-info-darken-100": isInfo && isOutline,
        },
      )

      return (
        <BadgeContext.Provider value={{ variation }}>
          <span ref={ref} className={_className} {...rest}>
            {children}
          </span>
        </BadgeContext.Provider>
      )
    },
  ),
  {
    Icon: BadgeIcon,
  },
)
